/**
 * AutoDiscoverArcGISDialog — modal that searches ArcGIS Hub for parcel
 * FeatureServers matching a county, shows ranked candidates with
 * auto-suggested field mappings, and promotes the dynamic county
 * registry entry to auto-fetchable in one click.
 *
 * Renders on top of AdminParcelV2County. Closes automatically after a
 * successful promotion (the parent re-fetches county detail).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Search, ExternalLink, CheckCircle2, XCircle, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { safeJsonFetch, authHeaders } from '../../lib/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;

const AutoDiscoverArcGISDialog = ({ countyId, countyName, onClose, onPromoted }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [query, setQuery] = useState('');
  const [promotingIdx, setPromotingIdx] = useState(null);
  const [successIdx, setSuccessIdx] = useState(null);

  const runDiscovery = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCandidates([]);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/discover-arcgis-url`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ county_id: countyId }),
    });
    setLoading(false);
    if (res.ok && res.data) {
      setQuery(res.data.query || '');
      setCandidates(res.data.candidates || []);
      if (!res.data.candidates || res.data.candidates.length === 0) {
        setError(
          `No public ArcGIS FeatureServers matched ${countyName}. ` +
          `Some counties only publish parcels via paid GIS portals or PDF exports — you can still upload a shapefile manually below.`
        );
      }
    } else {
      setError(res.data?.detail || res.error || `HTTP ${res.status}`);
    }
  }, [countyId, countyName]);

  useEffect(() => {
    runDiscovery();
  }, [runDiscovery]);

  const promote = async (cand, idx) => {
    setPromotingIdx(idx);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/promote-county`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        county_id: countyId,
        url: cand.url,
        max_records: 2000,
        id_field: cand.id_field || 'OBJECTID',
        address_fields: cand.address_fields || {},
        property_type_field: cand.property_type_field,
        residential_codes: null,
        residential_code_prefixes: null,
        where_clause: null,
        estimated_parcels: cand.parcel_count || 0,
        arcgis_title: cand.title,
      }),
    });
    setPromotingIdx(null);
    if (res.ok && res.data?.promoted) {
      setSuccessIdx(idx);
      // Give the success state a beat to render before closing
      setTimeout(() => {
        onPromoted?.(res.data);
        onClose?.();
      }, 1000);
    } else {
      setError(res.data?.detail || res.error || `HTTP ${res.status}`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="auto-discover-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-600" />
              Auto-discover ArcGIS URL
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Searching public ArcGIS Hub for a parcel FeatureServer matching <strong>{countyName}</strong>.
            </p>
            {query && (
              <p className="text-xs text-gray-400 mt-1">
                Query: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{query}</code>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            data-testid="auto-discover-close-btn"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="py-12 flex flex-col items-center justify-center gap-3 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p>Searching ArcGIS Online + probing FeatureServers…</p>
              <p className="text-xs text-gray-400">Usually 5–15 seconds. We probe up to 6 candidate services in parallel.</p>
            </div>
          )}

          {!loading && error && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 flex items-start gap-2" data-testid="auto-discover-error">
              <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>{error}</div>
            </div>
          )}

          {!loading && !error && candidates.length > 0 && (
            <div className="space-y-3" data-testid="auto-discover-candidates">
              <p className="text-sm text-gray-600">
                Found <strong>{candidates.length}</strong> candidate{candidates.length !== 1 ? 's' : ''}.
                Click <em>Use this</em> on the one with the right parcel count for {countyName}.
              </p>
              {candidates.map((c, idx) => {
                const isSuccess = successIdx === idx;
                const isPromoting = promotingIdx === idx;
                const isAddressOnly = !c.property_type_field; // some address layers have no parcel type
                return (
                  <div
                    key={`${c.feature_server_url}-${c.layer_id}`}
                    className={`border-2 rounded-xl p-4 transition-all ${
                      isSuccess ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                    data-testid={`auto-discover-candidate-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-gray-900 truncate">{c.title}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Owner: <code className="bg-gray-100 px-1 rounded text-[11px]">{c.owner || 'unknown'}</code>
                          {' · '}
                          Layer <strong>{c.layer_name}</strong> (id={c.layer_id})
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className="bg-blue-100 text-blue-800 border border-blue-200">
                          {(c.parcel_count || 0).toLocaleString()} parcels
                        </Badge>
                        <Badge className="bg-purple-100 text-purple-800 border border-purple-200">
                          score {c.score}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1 mt-2 bg-gray-50 rounded p-2">
                      <div className="flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        <a href={c.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate inline-block max-w-full" title={c.url}>
                          {c.url.replace('/query', '')}
                        </a>
                      </div>
                      <div>
                        <span className="font-semibold">Suggested fields:</span>{' '}
                        <code className="text-[11px]">id={c.id_field}</code>
                        {c.address_fields?.house_number && <code className="text-[11px] ml-2">house={c.address_fields.house_number}</code>}
                        {c.address_fields?.street_name && <code className="text-[11px] ml-2">street={c.address_fields.street_name}</code>}
                        {c.address_fields?.full_address && <code className="text-[11px] ml-2">full={c.address_fields.full_address}</code>}
                        {c.property_type_field && <code className="text-[11px] ml-2">type={c.property_type_field}</code>}
                      </div>
                      {c.sample && (
                        <div>
                          <span className="font-semibold">Sample:</span>{' '}
                          <span className="text-gray-700 inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400" />
                            {c.address_fields?.full_address && c.sample[c.address_fields.full_address]
                              ? c.sample[c.address_fields.full_address]
                              : `${c.sample[c.address_fields?.house_number] || ''} ${c.sample[c.address_fields?.street_name] || ''}`.trim() || '(no sample address)'}
                          </span>
                        </div>
                      )}
                      {isAddressOnly && (
                        <div className="text-amber-700 mt-1">
                          ⚠ No property-type field detected — every record will be ingested as residential (fine for an
                          address-only layer; for a tax-parcel layer you may want to add a residential filter later).
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex justify-end">
                      {isSuccess ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> Promoted!
                        </Badge>
                      ) : (
                        <Button
                          onClick={() => promote(c, idx)}
                          disabled={isPromoting || promotingIdx !== null}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`auto-discover-promote-btn-${idx}`}
                        >
                          {isPromoting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Promoting...</> : 'Use this'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            Promoting registers the URL + field mappings so Parcel Fetcher can pull the county automatically.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={runDiscovery} disabled={loading} data-testid="auto-discover-rerun-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Re-search
            </Button>
            <Button variant="outline" onClick={onClose} data-testid="auto-discover-cancel-btn">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoDiscoverArcGISDialog;
